/**
 * Typed API client.
 *
 * Handles token storage, a single in-flight refresh on 401 (so ten parallel
 * requests do not fire ten refreshes), and RFC 9457 problem-details errors.
 */

import type { ProblemDetails, TokenPair } from '@/types'

const BASE = import.meta.env.VITE_API_BASE_URL ?? '/api/v1'
const ACCESS_KEY = 'leadconnector.access'
const REFRESH_KEY = 'leadconnector.refresh'

export class ApiError extends Error {
  status: number
  problem: ProblemDetails | null

  constructor(message: string, status: number, problem: ProblemDetails | null = null) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.problem = problem
  }

  get fieldErrors(): Array<{ field: string; message: string }> {
    return this.problem?.errors ?? []
  }
}

export const tokens = {
  get access() {
    return localStorage.getItem(ACCESS_KEY)
  },
  get refresh() {
    return localStorage.getItem(REFRESH_KEY)
  },
  set(pair: TokenPair) {
    localStorage.setItem(ACCESS_KEY, pair.access_token)
    localStorage.setItem(REFRESH_KEY, pair.refresh_token)
  },
  clear() {
    localStorage.removeItem(ACCESS_KEY)
    localStorage.removeItem(REFRESH_KEY)
  },
}

type Listener = () => void
const unauthorizedListeners = new Set<Listener>()

export function onUnauthorized(listener: Listener): () => void {
  unauthorizedListeners.add(listener)
  return () => unauthorizedListeners.delete(listener)
}

let refreshInFlight: Promise<boolean> | null = null

async function refreshTokens(): Promise<boolean> {
  const refresh = tokens.refresh
  if (!refresh) return false

  // Collapse concurrent refreshes into one request.
  if (!refreshInFlight) {
    refreshInFlight = (async () => {
      try {
        const response = await fetch(`${BASE}/auth/refresh`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ refresh_token: refresh }),
        })
        if (!response.ok) return false
        tokens.set((await response.json()) as TokenPair)
        return true
      } catch {
        return false
      } finally {
        // Cleared on the next tick so callers awaiting this promise all see it.
        setTimeout(() => {
          refreshInFlight = null
        }, 0)
      }
    })()
  }
  return refreshInFlight
}

interface RequestOptions {
  method?: string
  body?: unknown
  params?: Record<string, string | number | boolean | undefined | null>
  retryOn401?: boolean
}

export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { method = 'GET', body, params, retryOn401 = true } = options

  const url = new URL(`${BASE}${path}`, window.location.origin)
  if (params) {
    for (const [key, value] of Object.entries(params)) {
      if (value !== undefined && value !== null && value !== '') {
        url.searchParams.set(key, String(value))
      }
    }
  }

  const headers: Record<string, string> = { Accept: 'application/json' }
  const access = tokens.access
  if (access) headers.Authorization = `Bearer ${access}`
  if (body !== undefined) headers['Content-Type'] = 'application/json'

  const response = await fetch(url.toString(), {
    method,
    headers,
    body: body === undefined ? undefined : JSON.stringify(body),
  })

  if (response.status === 401 && retryOn401 && !path.startsWith('/auth/')) {
    if (await refreshTokens()) {
      return request<T>(path, { ...options, retryOn401: false })
    }
    tokens.clear()
    unauthorizedListeners.forEach((listener) => listener())
    throw new ApiError('Your session has expired. Please sign in again.', 401)
  }

  if (response.status === 204) return undefined as T

  const text = await response.text()
  const payload = text ? safeJson(text) : null

  if (!response.ok) {
    const problem = (payload ?? null) as ProblemDetails | null
    throw new ApiError(
      problem?.detail || problem?.title || `Request failed (${response.status})`,
      response.status,
      problem,
    )
  }

  return payload as T
}

function safeJson(text: string): unknown {
  try {
    return JSON.parse(text)
  } catch {
    return null
  }
}

export const api = {
  get: <T,>(path: string, params?: RequestOptions['params']) =>
    request<T>(path, { params }),
  post: <T,>(path: string, body?: unknown) => request<T>(path, { method: 'POST', body }),
  patch: <T,>(path: string, body?: unknown) => request<T>(path, { method: 'PATCH', body }),
  del: <T,>(path: string) => request<T>(path, { method: 'DELETE' }),
}
